import { useState } from "react";
import { Upload, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, titleize } from "../../lib/api";

export const DOC_TYPES = {
  mortgage_statement: "Mortgage statement", promissory_note: "Promissory note", mortgage_deed_of_trust: "Mortgage/deed of trust",
  closing_disclosure: "Closing Disclosure", loan_estimate: "Loan Estimate", default_letter: "Default/breach letter", foreclosure_complaint: "Foreclosure complaint",
  summons: "Summons", lis_pendens: "Lis pendens", judgment: "Judgment", sale_notice: "Sale notice", reinstatement_quote: "Reinstatement quote",
  payoff_statement: "Payoff statement", property_tax_bill: "Property tax bill", tax_certificate: "Tax certificate information", tax_deed_notice: "Tax deed notice",
  hoa_statement: "HOA statement", hoa_lien: "HOA lien", insurance_declaration: "Homeowners insurance declaration", deed: "Deed", title_report: "Title report",
  appraisal: "Appraisal", cma: "CMA", repair_estimate: "Repair estimates", probate: "Probate documents", divorce_order: "Divorce orders",
  bankruptcy: "Bankruptcy documents", other: "Other relevant document",
};

const Extracted = ({ x }) => {
  if (!x) return null;
  const amounts = Object.entries(x.amounts || {}).filter(([, v]) => v !== null && v !== "");
  return (
    <div className="mt-3 space-y-1 text-sm text-slate-700" data-testid="doc-extracted">
      {x.summary && <p className="italic">{x.summary}</p>}
      {x.document_date && <p><b>Date:</b> {x.document_date}</p>}
      {x.case_number && <p><b>Case #:</b> {x.case_number}</p>}
      {x.property_address && <p><b>Property:</b> {x.property_address}</p>}
      {!!x.parties?.length && <p><b>Parties:</b> {x.parties.join(", ")}</p>}
      {amounts.map(([k, v]) => <p key={k}><b>{titleize(k)}:</b> {money(v)}</p>)}
      {!!x.account_ids?.length && <p><b>Accounts:</b> {x.account_ids.join(", ")}</p>}
      {x.deadlines?.map((d) => <p key={d.label + d.date} className="font-semibold text-red-700">Deadline: {d.label} — {d.date}</p>)}
      {!!x.important_terms?.length && <p><b>Terms:</b> {x.important_terms.join("; ")}</p>}
    </div>
  );
};

export const DocumentVault = ({ data, onChange }) => {
  const [type, setType] = useState("");
  const [busy, setBusy] = useState(false);
  const missing = data.analysis.docs_missing || [];

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (type) fd.append("doc_type", type);
    setBusy(true);
    try {
      const { data: res } = await api.post("/case/documents", fd);
      toast.success(`Joe read your ${DOC_TYPES[res.document.doc_type] || "document"}.`);
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="document-vault">
      {!!missing.length && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="docs-missing-list">
          <p className="font-display font-bold text-amber-900">Documents that would help verify your review</p>
          <div className="mt-2 flex flex-wrap gap-2">{missing.map((m) => (
            <button key={m} data-testid={`missing-doc-${m}`} onClick={() => setType(m)} className={`tap rounded-full border px-3 py-1 text-sm font-semibold ${type === m ? "border-amber-700 bg-amber-200" : "border-amber-300 bg-white"}`}>{DOC_TYPES[m]}</button>
          ))}</div>
        </div>
      )}
      <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-6">
        <label className="text-sm font-semibold text-slate-600">What kind of document is it? (optional — Joe will check)</label>
        <select data-testid="doc-type-select" value={type} onChange={(e) => setType(e.target.value)} className="mt-2 h-12 w-full rounded-xl border border-slate-300 bg-white px-3">
          <option value="">Let Joe figure it out</option>
          {Object.entries(DOC_TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className="tap mt-4 flex h-14 cursor-pointer items-center justify-center gap-2 rounded-xl bg-slate-900 font-display font-bold uppercase tracking-wide text-white">
          {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> Joe is reading…</> : <><Upload className="h-5 w-5" /> Upload PDF or photo</>}
          <input data-testid="doc-upload-input" type="file" accept="application/pdf,image/*,text/plain" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files[0])} />
        </label>
        <p className="mt-2 text-xs text-slate-500">Files are stored securely and only shared with a specialist if you give permission.</p>
      </div>
      <div className="grid gap-3">
        {data.documents.map((d) => (
          <div key={d.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`doc-card-${d.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 font-semibold"><FileText className="h-5 w-5 text-slate-500" />{DOC_TYPES[d.doc_type] || d.doc_type}</div>
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            </div>
            <p className="font-mono text-xs text-slate-500">{d.filename}</p>
            <Extracted x={d.extracted} />
          </div>
        ))}
      </div>
    </div>
  );
};
